import { useEffect, useState, type FormEvent } from 'react';
import { Button, Input, Label, cn } from '@clarix/ui';
import { useNavigate } from 'react-router-dom';
// import { Link } from 'react-router-dom';
import { useAuthStore } from '@clarix/auth-store';
import { useResolveWorkspace } from '@/core/api';
import { buildWorkspaceUrl } from '@/gateway/getWorkspaceSlug';
import { IS_SINGLE_HOST } from '@/config/env';

/**
 * The apex landing page: "which workspace?".
 *
 * ── Why the apex cannot simply show a login form ────────────────────────────
 * Clarix is database-per-workspace, and `public.User` lives INSIDE each
 * workspace's own database. With no slug there is no database in which to look
 * an email up, so the apex cannot authenticate anyone — not as a policy choice,
 * but structurally. It used to work only because one database served everyone,
 * and it guessed the workspace from the account's memberships (owned first, else
 * oldest) — which was also non-deterministic for anyone in two workspaces.
 *
 * So the apex asks which workspace BEFORE it asks who you are, and the login
 * form lives at `{slug}.clarix.sarvadhi.work/login`.
 *
 * Signup and onboarding stay here: a person creating their first workspace has
 * no slug yet, by definition.
 */

const SLUG_DEBOUNCE_MS = 350;

/** Accepts what someone might paste — a bare slug, or a whole workspace URL. */
function normalizeSlug(input: string): string {
  const trimmed = input.trim().toLowerCase();
  // "https://acme.clarix.sarvadhi.work/login" → "acme"
  const asUrl = trimmed.match(/^https?:\/\/([^./]+)\./);
  const raw = asUrl?.[1] ?? trimmed;
  return raw.replace(/[^a-z0-9-]/g, '').slice(0, 63);
}

type Status = 'idle' | 'checking' | 'found' | 'unavailable' | 'missing' | 'error';

export function WorkspacePickerPage() {
  const navigate = useNavigate();
  const setWorkspaceSlug = useAuthStore((s) => s.setWorkspaceSlug);

  const [slug, setSlug] = useState('');
  const [debouncedSlug, setDebouncedSlug] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSlug(normalizeSlug(slug)), SLUG_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [slug]);

  const resolved = useResolveWorkspace(debouncedSlug);

  const status: Status = (() => {
    if (normalizeSlug(slug).length < 2) return 'idle';
    if (resolved.isFetching) return 'checking';
    if (resolved.isError) return 'error';
    if (!resolved.data) return 'idle';
    if (!resolved.data.exists) return 'missing';
    if (!resolved.data.active) return 'unavailable';
    return 'found';
  })();

  const go = (target: string): void => {
    // Single-host dev has no per-workspace origin, so the slug is carried in the
    // session instead and the navigation stays in-SPA. Subdomain deployments do
    // the real cross-origin hop. Same branch every other caller here uses.
    if (IS_SINGLE_HOST) {
      setWorkspaceSlug(target);
      navigate('/login', { replace: true });
      return;
    }
    window.location.assign(buildWorkspaceUrl(target, '/login'));
  };

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setSubmitted(true);
    if (status === 'found' && resolved.data?.slug) go(resolved.data.slug);
  };

  const hint = (() => {
    switch (status) {
      case 'checking':
        return { tone: 'muted', text: 'Checking…' };
      case 'found':
        return { tone: 'ok', text: `Continue to ${resolved.data?.name ?? debouncedSlug}` };
      case 'missing':
        return { tone: 'error', text: 'No workspace with that name. Check the spelling.' };
      // Deliberately does not say WHY — the backend withholds suspended vs
      // archived vs provisioning from an unauthenticated caller, and so do we.
      case 'unavailable':
        return { tone: 'error', text: 'This workspace is unavailable. Contact your administrator.' };
      case 'error':
        return { tone: 'error', text: 'We could not check that just now. Please try again.' };
      default:
        return null;
    }
  })();

  return (
    <div>
      <h1 className="mb-1 text-5xl font-semibold text-slate-900 dark:text-slate-100">
        Welcome to Clarix
      </h1>
      <p className="mb-6 text-xl text-slate-500">Enter your workspace to continue.</p>

      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <div>
          <Label htmlFor="workspace">Workspace</Label>
          <Input
            id="workspace"
            name="workspace"
            autoFocus
            autoComplete="organization"
            placeholder="acme"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSubmitted(false);
            }}
            aria-invalid={(submitted && status !== 'found') || undefined}
            aria-describedby="workspace-hint"
          />
          <p
            id="workspace-hint"
            aria-live="polite"
            className={cn(
              'mt-1 text-lg',
              hint?.tone === 'error'
                ? 'text-red-600 dark:text-red-400'
                : hint?.tone === 'ok'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-slate-500',
            )}
          >
            {hint?.text ?? 'For example, acme'}
          </p>
        </div>

        <Button type="submit" className="w-full" disabled={status !== 'found'}>
          Continue
        </Button>
      </form>
       {/* <p className="mt-6 text-center text-xl text-slate-500">
        New to Clarix?{' '}
        <Link to="/signup" className="font-medium text-indigo-600 hover:underline">
          Create an account
        </Link>
      </p> */}
    </div>
  );
}
